import { useCallback, useEffect, useRef, useState } from 'react'
import { getCatchUpDigest } from '../services/api'
import { useTutorial } from '../contexts/TutorialContext'
import { useAuth } from '../contexts/AuthContext'

/**
 * Minimum away time (seconds) before a digest is worth showing.
 * Kept in sync with the plan's DIGEST_MIN_AWAY_SECONDS (Slice 7 makes it
 * server-configurable; the frontend threshold stays a constant per plan §5).
 */
export const DIGEST_MIN_AWAY_SECONDS = 300

/**
 * Fetches the offline catch-up digest once per authenticated session and
 * exposes it behind an open/dismiss gate.
 *
 * Rules (plan Slice 6):
 *  - fetch only when authenticated and the tutorial is not active
 *  - never opens on an empty digest (`has_content` false) or short absences
 *    (< 5 minutes away)
 *  - digest is consumed server-side on fetch, so a failure is opportunistic:
 *    log and stay silent
 */
export const useCatchUpDigest = () => {
    const { isAuthenticated } = useAuth()
    const { state: tutorialState, available: tutorialAvailable } = useTutorial()
    const [digest, setDigest] = useState(null)
    const [isOpen, setIsOpen] = useState(false)
    const requestedRef = useRef(false)

    // Same tutorial-active rule as TutorialRecoveryBeat / ActiveIdleRunView.
    const tutorialActive =
        !!tutorialAvailable &&
        tutorialState?.enabled !== false &&
        tutorialState?.status === 'active'

    useEffect(() => {
        if (!isAuthenticated || tutorialActive || requestedRef.current) return undefined

        requestedRef.current = true
        let cancelled = false

        getCatchUpDigest()
            .then(({ data }) => {
                if (cancelled || !data) return
                const eligible =
                    data.has_content &&
                    (typeof data.away_seconds === 'number' ? data.away_seconds : 0) >=
                        DIGEST_MIN_AWAY_SECONDS
                if (eligible) {
                    setDigest(data)
                    setIsOpen(true)
                }
            })
            .catch((error) => {
                // Digest is opportunistic — a failed fetch must never surface UI noise.
                console.warn('Catch-up digest unavailable:', error)
            })

        return () => {
            cancelled = true
        }
    }, [isAuthenticated, tutorialActive])

    const dismiss = useCallback(() => setIsOpen(false), [])

    return { digest, isOpen, dismiss }
}

export default useCatchUpDigest
